import {TFunction} from 'i18next';
import jsPDF from 'jspdf';
import autoTable, {Styles} from 'jspdf-autotable';
import moment from 'moment';
import {CarePlanConfigurationFeature} from '../../../../backend/enums/care-plan-configuration-feature';
import {PosologySchedule} from '../../../../backend/enums/posology-schedule';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {User} from '../../../../backend/interfaces/user';
import {PosologyScheduleFormType} from '../../../../types/schedule';
import {safeParseJson} from '../../../functions/safe-parse-json';
import {scheduleReadableExpression} from '../../../functions/schedule-readable-expression';
import {translations} from '../../../../i18n/translations';
import {PDF_FONT_FAMILY, PDF_FONT_MAIN_COLOR, registerPdfFonts} from '../base/export-pdf-font';
import {buildExportFileName} from '../base/export-file-name';
import type {PreparedPdfExportFile} from '../base/export-data-types';
import {pdfAddHorizontalLine} from '../base/export-pdf-footer-header-utils';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {createAutoTableRichTextHooks} from '../base/export-pdf-rich-text-autotable';
import {ExportCarePlanData, ExportCarePlanEntry} from '../care-plan/base/export-care-plan-types';
import {abbreviateMealName} from '../care-plan/clinical-support/medication/export-care-plan-medication-utils';
import {
  ExportSeniorProfileData,
  ExportSeniorProfileLatestVitalSignsData,
} from './base/export-senior-profile-types';
import {ActivityType} from '../../../../backend/interfaces/activity-type';

// -- pdf config --
const PDF_MARGIN_X = 14;
const PDF_CONTINUATION_MARGIN_TOP = 7;
const PDF_BOTTOM_MARGIN = 15;
// -- main title config --
const REPORT_TITLE_FONT_SIZE = 14;
const REPORT_TITLE_MARGIN_BOTTOM = 3.5;
// -- sections config --
const SECTIONS_GAP = 7;
const SECTION_TITLE_FONT_SIZE = 11.5;
const SECTION_SUBTITLE_FONT_SIZE = 9.8;
const SECTION_MARGIN_BOTTOM = 2.5;
const SECTION_TITLE_SUBTITLE_GAP = 1;
const SECTION_TITLE_PADDING_X = 1.5;
const SECTION_SUBTITLE_BACKGROUND_PAD = 3;
// -- profile fields config --
const PROFILE_FIELD_FONT_SIZE = 9.2;
const PROFILE_FIELD_EXTRA_LINE_HEIGHT = 0.6;
const PROFILE_FIELD_GAP = 1.9;
// -- food intolerances config --
const FOOD_INTOLERANCES_DETAIL_ID = '64871c4e39720427027c3746';
// -- medication allergies config --
const MEDICATION_ALLERGIES_DETAIL_ID = '69e60d64538871396a27214d';
// -- bullet list config --
const BULLET_LIST_FONT_SIZE = 9;
const BULLET_LIST_ITEM_GAP = 1.45;
// -- table config --
const TABLE_FONT_SIZE = 7.6;
const TABLE_HEAD_FONT_SIZE = 8;
// -- other config --
const HORIZONTAL_LINE_MARGIN_TOP = 3;
const SECTION_COLUMN_GAP = 4.5;
const EMPTY_TEXT_DEFAULT_POSX = PDF_MARGIN_X + 1.5;

export async function prepareSeniorProfileDataPdf({
  preparedData,
  entity,
  t,
}: {
  preparedData: ExportSeniorProfileData;
  entity: EntityInfo;
  t: TFunction;
}): Promise<PreparedPdfExportFile> {
  const pdf = new jsPDF({orientation: 'portrait', compress: true});
  await registerPdfFonts(pdf);
  const labels = geti18nLabels(t);

  const seniorUser = preparedData.userData.user.user;
  const seniorAffiliation = preparedData.userData.user.affiliation;
  const seniorName = seniorUser?.displayName ?? seniorUser?.name ?? '';

  pdf.setProperties({
    title: seniorName ? `${seniorName} - ${labels.reportTitle}` : labels.reportTitle,
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  // ---- User profile section ----
  let currentY = drawUserProfileSection(pdf, {
    user: seniorUser,
    additionalInfo: seniorAffiliation?.additionalInfo,
    labels,
  });

  // ---- Health conditions section ----
  currentY = drawHealthConditionsSection(
    pdf,
    preparedData.userData.healthConditions,
    currentY + SECTIONS_GAP,
    labels
  );

  // ---- Food intolerances section ----
  currentY = drawFoodIntolerancesSection(
    pdf,
    preparedData.foodCarePlanData,
    preparedData.medicationCarePlanData,
    currentY + SECTIONS_GAP,
    labels
  );

  // --- Medication section ----
  currentY = drawMedicationSection(
    pdf,
    preparedData.medicationCarePlanData,
    preparedData.mealActivityTypes,
    currentY + SECTIONS_GAP,
    labels
  );

  // --- Latest vital signs section ----
  drawLatestVitalSignsSection(
    pdf,
    preparedData.userData.lastestVitalSigns,
    currentY + SECTIONS_GAP,
    labels
  );

  decorateAllPages(pdf, seniorUser, entity, labels);

  const fileName = buildExportFileName(
    [seniorUser.displayName ?? seniorUser.name, t(translations.export.senior_profile.report_title)],
    {date: 'day'}
  );

  return {
    file: pdf,
    fileName,
  };
}

// --------------------------------------------
//           USER PROFILE SECTION
// --------------------------------------------
function drawUserProfileSection(
  pdf: jsPDF,
  {
    user,
    additionalInfo,
    labels,
  }: {
    user: User;
    additionalInfo?: string;
    labels: SeniorProfilePdfLabels;
  }
): number {
  const pageWidth = pdf.internal.pageSize.width;
  const contentWidth = pageWidth - PDF_MARGIN_X * 2;
  const columnGap = 8;
  const columnWidth = (contentWidth - columnGap) / 2;
  const leftColumnX = EMPTY_TEXT_DEFAULT_POSX;
  const rightColumnX = leftColumnX + columnWidth + columnGap;
  const reportTitleY = 24;
  const sectionTitleY =
    reportTitleY + getPdfFontHeight(pdf, REPORT_TITLE_FONT_SIZE) + REPORT_TITLE_MARGIN_BOTTOM;

  const leftFields: ProfileField[] = [
    {label: labels.fullName, value: user.name},
    {label: labels.preferredName, value: user.displayName},
    {label: labels.birthDate, value: formatBirthdayWithAge(user.birthday, labels.years)},
    {label: labels.address, value: normalizeText(user.address)},
  ];

  const rightFields: ProfileField[] = [
    {label: labels.snsNumber, value: user.nationalHealthServiceNumber},
    {label: labels.socialSecurityNumber, value: user.socialSecurityNumber},
    {label: labels.taxpayerNumber, value: user.taxPayerNumber},
  ];

  pdf.setFont(PDF_FONT_FAMILY, 'bold');
  pdf.setFontSize(REPORT_TITLE_FONT_SIZE);
  pdf.text(labels.reportTitle, PDF_MARGIN_X, reportTitleY, {baseline: 'top'});

  const {bodyStartY} = drawSectionHeader(pdf, {
    title: labels.userSectionTitle,
    subtitle: labels.profileSectionSubtitle,
    titleY: sectionTitleY,
    contentWidth,
  });

  let leftCursorY = bodyStartY;
  for (const field of leftFields) {
    leftCursorY = drawInlineField(pdf, field, leftColumnX, leftCursorY, columnWidth);
    leftCursorY += PROFILE_FIELD_GAP;
  }

  let rightCursorY = bodyStartY;
  for (const field of rightFields) {
    rightCursorY = drawInlineField(pdf, field, rightColumnX, rightCursorY, columnWidth);
    rightCursorY += PROFILE_FIELD_GAP;
  }

  const additionalInfoY = Math.max(leftCursorY, rightCursorY) + PROFILE_FIELD_GAP * 1.5;
  const additionalInfoHeight = drawInlineField(
    pdf,
    {
      label: labels.additionalInformation,
      value: normalizeText(additionalInfo),
    },
    leftColumnX,
    additionalInfoY,
    contentWidth + PDF_MARGIN_X
  );

  const finalY = pdfAddHorizontalLine(
    pdf,
    PDF_MARGIN_X,
    additionalInfoHeight + HORIZONTAL_LINE_MARGIN_TOP
  );

  return finalY;
}

// --------------------------------------------
//           HEALTH CONDITIONS SECTION
// --------------------------------------------
function drawHealthConditionsSection(
  pdf: jsPDF,
  healthConditions: string[],
  startY: number,
  labels: SeniorProfilePdfLabels
): number {
  const pageWidth = pdf.internal.pageSize.width;
  const contentWidth = pageWidth - PDF_MARGIN_X * 2;
  const columnWidth = contentWidth / 2;
  const leftColumnX = EMPTY_TEXT_DEFAULT_POSX;
  const rightColumnX = leftColumnX + columnWidth;
  const conditions = healthConditions
    .map(normalizeText)
    .filter((item): item is string => Boolean(item))
    .sort((conditionA, conditionB) =>
      conditionA.localeCompare(conditionB, undefined, {sensitivity: 'base'})
    );
  const lineHeight = getPdfFontHeight(pdf, BULLET_LIST_FONT_SIZE);
  const bulletOffset = lineHeight;
  const estimatedHeight = getSectionEstimatedHeight(
    conditions.length > 0
      ? estimateTwoColumnBulletListHeight(pdf, conditions, columnWidth - bulletOffset, lineHeight)
      : lineHeight
  );
  const titleY = ensurePageSpace(pdf, startY, estimatedHeight);
  const {bodyStartY} = drawSectionHeader(pdf, {
    title: labels.details,
    subtitle: labels.healthConditionsSubtitle,
    titleY,
    contentWidth,
  });

  if (conditions.length === 0) {
    const finalY = drawEmptySectionText(
      pdf,
      labels.noRecords,
      EMPTY_TEXT_DEFAULT_POSX,
      bodyStartY,
      contentWidth
    );
    pdfAddHorizontalLine(pdf, PDF_MARGIN_X, finalY + HORIZONTAL_LINE_MARGIN_TOP);

    return finalY + 4;
  }

  const bulletMaxY = drawBulletListRows(
    pdf,
    conditions,
    leftColumnX,
    rightColumnX,
    bodyStartY,
    columnWidth - bulletOffset
  );

  const finalY = pdfAddHorizontalLine(pdf, PDF_MARGIN_X, bulletMaxY + HORIZONTAL_LINE_MARGIN_TOP);

  return finalY + HORIZONTAL_LINE_MARGIN_TOP;
}

// --------------------------------------------
//           FOOD INTOLERANCES SECTION
// --------------------------------------------
function drawFoodIntolerancesSection(
  pdf: jsPDF,
  foodCarePlanData: ExportCarePlanData | undefined,
  medicationCarePlanData: ExportCarePlanData | undefined,
  startY: number,
  labels: SeniorProfilePdfLabels
): number {
  const pageWidth = pdf.internal.pageSize.width;
  const contentWidth = pageWidth - PDF_MARGIN_X * 2;
  const columnWidth = (contentWidth - SECTION_COLUMN_GAP) / 2;
  const leftColumnX = PDF_MARGIN_X;
  const rightColumnX = leftColumnX + columnWidth + SECTION_COLUMN_GAP;
  const intoleranceItems = getFoodIntoleranceItems(foodCarePlanData).sort((itemA, itemB) =>
    itemA.localeCompare(itemB, undefined, {sensitivity: 'base'})
  );
  const medicationAllergyItems = getMedicationAllergyItems(medicationCarePlanData).sort(
    (itemA, itemB) => itemA.localeCompare(itemB, undefined, {sensitivity: 'base'})
  );
  const lineHeight = getPdfFontHeight(pdf, BULLET_LIST_FONT_SIZE);
  const listWidth = columnWidth - SECTION_TITLE_PADDING_X * 2;
  const intoleranceColumnHeight = estimateTitledListColumnHeight(
    pdf,
    intoleranceItems,
    listWidth,
    lineHeight,
    labels.noRecords
  );
  const medicationAllergyColumnHeight = estimateTitledListColumnHeight(
    pdf,
    medicationAllergyItems,
    listWidth,
    lineHeight,
    labels.noRecords
  );
  const estimatedHeight = getSectionEstimatedHeight(
    Math.max(intoleranceColumnHeight, medicationAllergyColumnHeight),
    false
  );
  const titleY = ensurePageSpace(pdf, startY, estimatedHeight);

  // Draw section header
  const {bodyStartY} = drawSectionHeader(pdf, {
    title: labels.foodIntolerancesTitle,
    titleY,
    contentWidth,
  });

  // Draw Left column - Food intolerances
  const leftColumnFinalY = drawTitledListColumn(pdf, {
    title: labels.foodIntolerancesSubtitle,
    items: intoleranceItems,
    x: leftColumnX,
    y: bodyStartY,
    width: columnWidth,
    emptyText: labels.noRecords,
  });

  // Draw Right column - Medication allergies
  const rightColumnFinalY = drawTitledListColumn(pdf, {
    title: labels.medicationAllergiesSubtitle,
    items: medicationAllergyItems,
    x: rightColumnX,
    y: bodyStartY,
    width: columnWidth,
    emptyText: labels.noRecords,
  });

  // Draw horizontal line at the end of the section
  const finalY = pdfAddHorizontalLine(
    pdf,
    PDF_MARGIN_X,
    Math.max(leftColumnFinalY, rightColumnFinalY) + HORIZONTAL_LINE_MARGIN_TOP
  );

  return finalY;
}

// --------------------------------------------
//           MEDICATION SECTION
// --------------------------------------------
function drawMedicationSection(
  pdf: jsPDF,
  medicationCarePlanData: ExportCarePlanData | undefined,
  mealActivityTypes: ActivityType[],
  startY: number,
  labels: SeniorProfilePdfLabels
): number {
  const contentWidth = pdf.internal.pageSize.width - PDF_MARGIN_X * 2;
  const titleY = ensurePageSpace(pdf, startY, getSectionEstimatedHeight(12, false));
  const {headers, rows} = buildMedicationTable(medicationCarePlanData, mealActivityTypes, labels);
  const richTextHooks = createAutoTableRichTextHooks({pdf});
  const {bodyStartY} = drawSectionHeader(pdf, {
    title: labels.medicationTitle,
    titleY,
    contentWidth,
  });

  if (rows.length === 0) {
    const emptyTextY = drawEmptySectionText(
      pdf,
      labels.noRecords,
      EMPTY_TEXT_DEFAULT_POSX,
      bodyStartY + 1.3,
      contentWidth
    );
    const finalY = pdfAddHorizontalLine(pdf, PDF_MARGIN_X, emptyTextY + HORIZONTAL_LINE_MARGIN_TOP);

    return finalY;
  }

  autoTable(pdf, {
    startY: bodyStartY,
    head: [headers],
    body: rows,
    margin: {
      right: PDF_MARGIN_X,
      left: PDF_MARGIN_X,
    },
    tableWidth: pdf.internal.pageSize.width - PDF_MARGIN_X * 2,
    rowPageBreak: 'avoid',
    styles: {
      font: PDF_FONT_FAMILY,
      fontSize: TABLE_FONT_SIZE,
      valign: 'middle',
      cellPadding: 1.1,
    },
    headStyles: {
      font: PDF_FONT_FAMILY,
      fontStyle: 'bold',
      fontSize: TABLE_HEAD_FONT_SIZE,
    },
    columnStyles: buildMedicationColumnStyles(mealActivityTypes.length),
    willDrawPage: function (data) {
      if (data.pageNumber === 1) {
        data.settings.margin.top = PDF_CONTINUATION_MARGIN_TOP;
      }
    },
    didParseCell: (data) => {
      richTextHooks.didParseCell(data);
    },
    willDrawCell: (data) => {
      richTextHooks.willDrawCell(data);
    },
    didDrawCell: (data) => {
      richTextHooks.didDrawCell(data);
    },
  });

  return (pdf as any).lastAutoTable?.finalY ?? bodyStartY;
}

// --------------------------------------------
//           LATEST VITAL SIGNS SECTION
// --------------------------------------------
function drawLatestVitalSignsSection(
  pdf: jsPDF,
  latestVitalSigns: ExportSeniorProfileLatestVitalSignsData[],
  startY: number,
  labels: SeniorProfilePdfLabels
): number {
  const contentWidth = pdf.internal.pageSize.width - PDF_MARGIN_X * 2;
  const titleY = ensurePageSpace(pdf, startY, getSectionEstimatedHeight(12, false));
  const rows = buildVitalSignsRows(latestVitalSigns);
  const {bodyStartY} = drawSectionHeader(pdf, {
    title: labels.latestVitalSignsTitle,
    titleY,
    contentWidth,
  });

  if (rows.length === 0) {
    const emptyTextY = drawEmptySectionText(
      pdf,
      labels.noRecords,
      EMPTY_TEXT_DEFAULT_POSX,
      bodyStartY + 1.3,
      contentWidth
    );
    const finalY = pdfAddHorizontalLine(pdf, PDF_MARGIN_X, emptyTextY + HORIZONTAL_LINE_MARGIN_TOP);

    return finalY;
  }

  autoTable(pdf, {
    startY: bodyStartY,
    head: [[labels.date, labels.task, labels.register, labels.observations]],
    body: rows,
    margin: {
      right: PDF_MARGIN_X,
      left: PDF_MARGIN_X,
    },
    tableWidth: pdf.internal.pageSize.width - PDF_MARGIN_X * 2,
    rowPageBreak: 'avoid',
    styles: {
      font: PDF_FONT_FAMILY,
      fontSize: TABLE_FONT_SIZE,
      valign: 'middle',
      cellPadding: 1.2,
    },
    headStyles: {
      font: PDF_FONT_FAMILY,
      fontStyle: 'bold',
      fontSize: TABLE_HEAD_FONT_SIZE,
    },
    columnStyles: {
      // date
      0: {cellWidth: 28},
      // task
      1: {cellWidth: 40},
      // register
      2: {cellWidth: 40},
      // notes
      // 3: {cellWidth: 97},
    },
    willDrawPage: function (data) {
      if (data.pageNumber === 1) {
        data.settings.margin.top = PDF_CONTINUATION_MARGIN_TOP;
      }
    },
  });

  return (pdf as any).lastAutoTable?.finalY ?? bodyStartY;
}

// --------------------------------------------
//                   UTILS
// --------------------------------------------
function getSectionEstimatedHeight(bodyHeight: number, hasSubtitle = true): number {
  if (!hasSubtitle) {
    return SECTION_TITLE_SUBTITLE_GAP + bodyHeight;
  }

  return SECTION_TITLE_SUBTITLE_GAP + SECTION_MARGIN_BOTTOM + bodyHeight;
}

function drawSectionHeader(
  pdf: jsPDF,
  {
    title,
    subtitle,
    titleY,
    contentWidth,
  }: {
    title: string;
    subtitle?: string;
    titleY: number;
    contentWidth: number;
  }
): SectionHeaderLayout {
  pdf.setFont(PDF_FONT_FAMILY, 'bold');
  pdf.setFontSize(SECTION_TITLE_FONT_SIZE);
  pdf.text(title, PDF_MARGIN_X + SECTION_TITLE_PADDING_X, titleY, {baseline: 'top'});

  if (!subtitle) {
    return {
      titleY,
      bodyStartY:
        titleY + getPdfFontHeight(pdf, SECTION_TITLE_FONT_SIZE) + SECTION_TITLE_SUBTITLE_GAP,
    };
  }

  const subtitleY =
    titleY + getPdfFontHeight(pdf, SECTION_TITLE_FONT_SIZE) + SECTION_TITLE_SUBTITLE_GAP;
  const subtitleBarHeight = getSectionSubtitleBarHeight(pdf);
  const bodyStartY = subtitleY + SECTION_MARGIN_BOTTOM + subtitleBarHeight;

  pdf.setFillColor(41, 128, 185);
  pdf.rect(PDF_MARGIN_X, subtitleY, contentWidth, subtitleBarHeight, 'F');
  pdf.setFont(PDF_FONT_FAMILY, 'bold');
  pdf.setFontSize(SECTION_SUBTITLE_FONT_SIZE);
  pdf.setTextColor('#ffffff');
  pdf.text(
    subtitle,
    PDF_MARGIN_X + SECTION_TITLE_PADDING_X,
    subtitleY + SECTION_SUBTITLE_BACKGROUND_PAD / 2,
    {
      baseline: 'top',
    }
  );
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  return {
    titleY,
    subtitleY,
    bodyStartY,
  };
}

function getPdfFontHeight(pdf: jsPDF, fontSize: number): number {
  return fontSize / pdf.internal.scaleFactor;
}

function getSectionSubtitleBarHeight(pdf: jsPDF): number {
  return getPdfFontHeight(pdf, SECTION_SUBTITLE_FONT_SIZE) + SECTION_SUBTITLE_BACKGROUND_PAD;
}

function drawInlineField(
  pdf: jsPDF,
  field: ProfileField,
  x: number,
  y: number,
  width: number
): number {
  const labelText = `${field.label}: `;
  const valueText = normalizeText(field.value) || '-';
  const lineHeight =
    getPdfFontHeight(pdf, PROFILE_FIELD_FONT_SIZE) + PROFILE_FIELD_EXTRA_LINE_HEIGHT;

  pdf.setFont(PDF_FONT_FAMILY, 'bold');
  pdf.setFontSize(PROFILE_FIELD_FONT_SIZE);
  const labelWidth = pdf.getTextWidth(labelText);
  const valueLines = wrapTextForInlineField(pdf, valueText, width - labelWidth, width);

  pdf.text(labelText, x, y, {baseline: 'top'});

  pdf.setFont(PDF_FONT_FAMILY, 'normal');
  pdf.text(valueLines[0], x + labelWidth, y, {baseline: 'top'});

  for (let index = 1; index < valueLines.length; index += 1) {
    pdf.text(valueLines[index], x, y + lineHeight * index, {baseline: 'top'});
  }

  return y + lineHeight * valueLines.length;
}

function drawBulletListColumn(
  pdf: jsPDF,
  items: string[],
  x: number,
  y: number,
  width: number
): number {
  let cursorY = y;

  pdf.setFont(PDF_FONT_FAMILY, 'normal');
  pdf.setFontSize(BULLET_LIST_FONT_SIZE);

  for (const item of items) {
    const wrappedLines = pdf.splitTextToSize(item || '-', width) as string[];
    const lineHeight = getPdfFontHeight(pdf, BULLET_LIST_FONT_SIZE);
    const bulletOffset = lineHeight;

    pdf.text('•', x, cursorY, {baseline: 'top'});
    pdf.text(wrappedLines[0], x + bulletOffset, cursorY, {baseline: 'top'});

    for (let index = 1; index < wrappedLines.length; index += 1) {
      pdf.text(wrappedLines[index], x + bulletOffset, cursorY + lineHeight * index, {
        baseline: 'top',
      });
    }

    cursorY += lineHeight * wrappedLines.length + BULLET_LIST_ITEM_GAP;
  }

  return cursorY;
}

function drawBulletListRows(
  pdf: jsPDF,
  items: string[],
  leftX: number,
  rightX: number,
  y: number,
  width: number
): number {
  let cursorY = y;

  pdf.setFont(PDF_FONT_FAMILY, 'normal');
  pdf.setFontSize(BULLET_LIST_FONT_SIZE);

  for (let index = 0; index < items.length; index += 2) {
    const leftLines = getBulletListWrappedLines(pdf, items[index], width);
    const rightLines = items[index + 1]
      ? getBulletListWrappedLines(pdf, items[index + 1], width)
      : undefined;

    drawBulletListItem(pdf, leftLines, leftX, cursorY);

    if (rightLines) {
      drawBulletListItem(pdf, rightLines, rightX, cursorY);
    }

    cursorY +=
      Math.max(leftLines.length, rightLines?.length ?? 0, 1) *
        getPdfFontHeight(pdf, BULLET_LIST_FONT_SIZE) +
      BULLET_LIST_ITEM_GAP;
  }

  return cursorY;
}

function drawEmptySectionText(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number
): number {
  pdf.setFont(PDF_FONT_FAMILY, 'normal');
  pdf.setFontSize(BULLET_LIST_FONT_SIZE);

  const lineHeight = getPdfFontHeight(pdf, BULLET_LIST_FONT_SIZE);
  const lines = pdf.splitTextToSize(text, width);
  pdf.text(lines, x, y, {baseline: 'top'});

  return y + lines.length * lineHeight;
}

function drawTitledListColumn(
  pdf: jsPDF,
  {
    title,
    items,
    x,
    y,
    width,
    emptyText,
  }: {
    title: string;
    items: string[];
    x: number;
    y: number;
    width: number;
    emptyText: string;
  }
): number {
  const listStartY = drawSectionSubtitleBar(pdf, {title, x, y, width});
  const contentX = x + SECTION_TITLE_PADDING_X;
  const contentWidth = width - SECTION_TITLE_PADDING_X * 2;

  if (items.length === 0) {
    return drawEmptySectionText(pdf, emptyText, contentX, listStartY, contentWidth);
  }

  return drawBulletListColumn(pdf, items, contentX, listStartY, contentWidth);
}

function drawSectionSubtitleBar(
  pdf: jsPDF,
  {
    title,
    x,
    y,
    width,
  }: {
    title: string;
    x: number;
    y: number;
    width: number;
  }
): number {
  const subtitleBarHeight = getSectionSubtitleBarHeight(pdf);

  pdf.setFillColor(41, 128, 185);
  pdf.rect(x, y, width, subtitleBarHeight, 'F');
  pdf.setFont(PDF_FONT_FAMILY, 'bold');
  pdf.setFontSize(SECTION_SUBTITLE_FONT_SIZE);
  pdf.setTextColor('#ffffff');
  pdf.text(title, x + SECTION_TITLE_PADDING_X, y + SECTION_SUBTITLE_BACKGROUND_PAD / 2, {
    baseline: 'top',
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  return y + subtitleBarHeight + SECTION_MARGIN_BOTTOM;
}

function drawBulletListItem(pdf: jsPDF, wrappedLines: string[], x: number, y: number): void {
  const lineHeight = getPdfFontHeight(pdf, BULLET_LIST_FONT_SIZE);
  const bulletOffset = lineHeight;

  pdf.text('•', x, y, {baseline: 'top'});
  pdf.text(wrappedLines[0], x + bulletOffset, y, {baseline: 'top'});

  for (let index = 1; index < wrappedLines.length; index += 1) {
    pdf.text(wrappedLines[index], x + bulletOffset, y + lineHeight * index, {
      baseline: 'top',
    });
  }
}

function getBulletListWrappedLines(pdf: jsPDF, item: string, width: number): string[] {
  return pdf.splitTextToSize(item || '-', width) as string[];
}

function estimateTwoColumnBulletListHeight(
  pdf: jsPDF,
  items: string[],
  width: number,
  lineHeight: number
): number {
  let height = 0;

  for (let index = 0; index < items.length; index += 2) {
    const leftLines = getBulletListWrappedLines(pdf, items[index], width);
    const rightLines = items[index + 1]
      ? getBulletListWrappedLines(pdf, items[index + 1], width)
      : [];

    height += Math.max(leftLines.length, rightLines.length, 1) * lineHeight + BULLET_LIST_ITEM_GAP;
  }

  return height;
}

function estimateColumnHeight(
  pdf: jsPDF,
  items: string[],
  width: number,
  lineHeight: number
): number {
  return items.reduce((height, item) => {
    const wrappedLines = pdf.splitTextToSize(item, width) as string[];
    return height + Math.max(wrappedLines.length, 1) * lineHeight + BULLET_LIST_ITEM_GAP;
  }, 0);
}

function estimateTitledListColumnHeight(
  pdf: jsPDF,
  items: string[],
  width: number,
  lineHeight: number,
  emptyText: string
): number {
  const titleBarHeight = getSectionSubtitleBarHeight(pdf) + SECTION_MARGIN_BOTTOM;
  const contentHeight =
    items.length > 0
      ? estimateColumnHeight(pdf, items, width, lineHeight)
      : estimateEmptyTextHeight(pdf, emptyText, width, lineHeight);

  return titleBarHeight + contentHeight;
}

function estimateEmptyTextHeight(
  pdf: jsPDF,
  text: string,
  width: number,
  lineHeight: number
): number {
  const lines = pdf.splitTextToSize(text, width) as string[];
  return Math.max(lines.length, 1) * lineHeight;
}

function getFoodIntoleranceItems(foodCarePlanData: ExportCarePlanData | undefined): string[] {
  return getCarePlanDetailItems(foodCarePlanData, FOOD_INTOLERANCES_DETAIL_ID);
}

function getMedicationAllergyItems(
  medicationCarePlanData: ExportCarePlanData | undefined
): string[] {
  return getCarePlanDetailItems(medicationCarePlanData, MEDICATION_ALLERGIES_DETAIL_ID);
}

function getCarePlanDetailItems(
  carePlanData: ExportCarePlanData | undefined,
  detailId: string
): string[] {
  if (!carePlanData) {
    return [];
  }

  const detail = carePlanData.details.find((entry) => entry.id === detailId);

  if (!detail) {
    return [];
  }

  return normalizeEntryValueToList(detail.value);
}

function normalizeEntryValueToList(value: string | string[]): string[] {
  if (Array.isArray(value)) {
    return value.map(normalizeText).filter((item): item is string => Boolean(item));
  }

  return value
    .split(/\r?\n|;/)
    .map(normalizeText)
    .filter((item): item is string => Boolean(item));
}

function buildMedicationTable(
  medicationCarePlanData: ExportCarePlanData | undefined,
  mealActivityTypes: ActivityType[],
  labels: SeniorProfilePdfLabels
): {
  headers: string[];
  rows: string[][];
} {
  const mealHeaders = mealActivityTypes.map((activityType) =>
    abbreviateMealName(activityType.name)
  );
  const headers = [
    labels.startDate,
    labels.endDate,
    labels.medicationTitle,
    ...mealHeaders,
    labels.others,
    labels.observations,
  ];
  const tasks = medicationCarePlanData?.tasks.filter((task) => task.length > 0) ?? [];

  if (tasks.length === 0) {
    return {
      headers,
      rows: [],
    };
  }

  return {
    headers,
    rows: tasks.map((task) => buildMedicationRow(task, mealActivityTypes, labels)),
  };
}

function buildMedicationRow(
  task: ExportCarePlanEntry[],
  mealActivityTypes: ActivityType[],
  labels: SeniorProfilePdfLabels
): string[] {
  const schedule = safeParseJson<PosologyScheduleFormType>(
    getTaskRawValue(task, CarePlanConfigurationFeature.Schedule) as string | object | undefined
  );
  const startDate = formatDate(
    getTaskValue(task, CarePlanConfigurationFeature.StartsAtDate) ??
      getTaskValue(task, CarePlanConfigurationFeature.StartsAtTime)
  );
  const endDate = formatDate(schedule?.endDate);
  const medication = formatMedicationDetails(task);
  const notes = getTaskValue(task, CarePlanConfigurationFeature.Notes) ?? '';
  const mealValues = mealActivityTypes.map((mealActivityType) => {
    if (schedule?.posologyScheduleType !== PosologySchedule.PeriodsOfTheDay) {
      return '';
    }

    const period = schedule.periodsOfTheDay?.find(
      (item) => item.activityTypeId === mealActivityType.id && item.quantity !== 0
    );

    return period ? String(period.quantity) : '';
  });
  const other =
    schedule?.posologyScheduleType === PosologySchedule.PeriodsOfTheDay
      ? ''
      : formatMedicationSchedule(schedule, labels.t);

  return [startDate, endDate, medication, ...mealValues, other, notes];
}

function buildMedicationColumnStyles(mealCount: number): {[key: string]: Partial<Styles>} {
  const columnStyles: {[key: string]: Partial<Styles>} = {
    // start date
    0: {cellWidth: 16},
    // end date
    1: {cellWidth: 16},
    // medication
    2: {cellWidth: 35},
    // other (start date + end date + medication = 3 + mealCount)
    [3 + mealCount]: {cellWidth: 35},
  };

  // meal columns
  for (let index = 0; index < mealCount; index += 1) {
    columnStyles[index + 3] = {cellWidth: 6};
  }

  return columnStyles;
}

function buildVitalSignsRows(
  latestVitalSigns: ExportSeniorProfileData['userData']['lastestVitalSigns']
): string[][] {
  const sortedEntries = [...latestVitalSigns].sort((entryA, entryB) => {
    const dateA = new Date(entryA.measurement.startedAt ?? entryA.measurement.receivedAt).getTime();
    const dateB = new Date(entryB.measurement.startedAt ?? entryB.measurement.receivedAt).getTime();
    return dateB - dateA;
  });

  if (sortedEntries.length === 0) {
    return [];
  }

  return sortedEntries.map((entry) => [
    formatDateTime(entry.measurement.startedAt ?? entry.measurement.receivedAt),
    normalizeText(entry.activityType.name) ?? '-',
    normalizeText(entry.registerText) ?? '-',
    normalizeText(entry.measurement.comment) ?? '',
  ]);
}

function formatMedicationDetails(task: ExportCarePlanEntry[]): string {
  const medicine = getTaskValue(task, CarePlanConfigurationFeature.Medicine);
  const taskName = getTaskValue(task, CarePlanConfigurationFeature.Name);
  const dosage = getTaskValue(task, CarePlanConfigurationFeature.MedicineDosage);
  const dosageUnit = getTaskValue(task, CarePlanConfigurationFeature.MedicineDosageUnit);
  const administrationRoute = getTaskValue(
    task,
    CarePlanConfigurationFeature.MedicineAdministrationRoute
  );
  const firstLine = medicine ?? taskName ?? '-';
  const dosageText = [dosage, dosageUnit].filter(Boolean).join(' ');
  const secondLine = dosageText
    ? [dosageText, administrationRoute].filter(Boolean).join(', ')
    : administrationRoute;
  const formattedFirstLine = `<b>${firstLine}</b>`;

  return secondLine ? `${formattedFirstLine}\n${secondLine}` : formattedFirstLine;
}

function formatMedicationSchedule(
  schedule: PosologyScheduleFormType | undefined,
  t: TFunction
): string {
  if (!schedule) {
    return '';
  }

  return scheduleReadableExpression({t, scheduleDefinition: schedule}) ?? '';
}

function getTaskValue(
  task: ExportCarePlanEntry[],
  feature: CarePlanConfigurationFeature
): string | undefined {
  const entry = task.find((taskEntry) => taskEntry.feature === feature);
  return entryValueToString(entry?.value);
}

function getTaskRawValue(
  task: ExportCarePlanEntry[],
  feature: CarePlanConfigurationFeature
): string | string[] | undefined {
  return task.find((taskEntry) => taskEntry.feature === feature)?.value;
}

function entryValueToString(value?: string | string[]): string | undefined {
  if (Array.isArray(value)) {
    return normalizeText(value.join(', '));
  }

  return normalizeText(value);
}

function ensurePageSpace(pdf: jsPDF, startY: number, requiredHeight: number): number {
  const pageHeight = pdf.internal.pageSize.height;
  const maxY = pageHeight - PDF_BOTTOM_MARGIN;

  if (startY + requiredHeight <= maxY) {
    return startY;
  }

  pdf.addPage();
  return PDF_CONTINUATION_MARGIN_TOP;
}

function decorateAllPages(
  pdf: jsPDF,
  seniorUser: User,
  entity: EntityInfo,
  labels: SeniorProfilePdfLabels
) {
  const numberOfPages = pdf.getNumberOfPages();

  for (let pageNumber = 1; pageNumber <= numberOfPages; pageNumber += 1) {
    pdf.setPage(pageNumber);
    pdf.setTextColor(PDF_FONT_MAIN_COLOR);

    if (pageNumber === 1) {
      pdfHeaderInfo(pdf, seniorUser, entity, labels.t);
    }

    pdfFooterInfo(pdf, `${pageNumber}`);
  }
}

function wrapTextForInlineField(
  pdf: jsPDF,
  text: string,
  firstLineWidth: number,
  nextLineWidth: number
): string[] {
  const tokens = text.split(/\s+/).filter(Boolean);

  if (tokens.length === 0) {
    return ['-'];
  }

  const lines: string[] = [];
  let currentLine = '';
  let currentWidthLimit = Math.max(firstLineWidth, 10);

  const pushCurrentLine = () => {
    if (currentLine) {
      lines.push(currentLine);
      currentLine = '';
      currentWidthLimit = Math.max(nextLineWidth, 10);
    }
  };

  for (const token of tokens) {
    const candidateLine = currentLine ? `${currentLine} ${token}` : token;
    const candidateWidth = pdf.getTextWidth(candidateLine);

    if (candidateWidth <= currentWidthLimit) {
      currentLine = candidateLine;
      continue;
    }

    if (!currentLine) {
      const brokenTokenLines = pdf.splitTextToSize(token, currentWidthLimit) as string[];
      if (brokenTokenLines.length === 0) {
        continue;
      }

      lines.push(brokenTokenLines[0]);
      currentWidthLimit = Math.max(nextLineWidth, 10);

      for (let index = 1; index < brokenTokenLines.length; index += 1) {
        lines.push(brokenTokenLines[index]);
      }
      continue;
    }

    pushCurrentLine();
    currentLine = token;
  }

  pushCurrentLine();

  return lines;
}

function formatDate(value?: string | Date): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('DD/MM/YYYY') : String(value);
}

function formatDateTime(value?: string | Date): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : String(value);
}

function formatBirthdayWithAge(birthday?: string, yearsLabel?: string): string | undefined {
  if (!birthday) {
    return undefined;
  }

  const birthdayMoment = moment(birthday);

  if (!birthdayMoment.isValid()) {
    return birthday;
  }

  const age = moment().diff(birthdayMoment, 'years');
  return `${birthdayMoment.format('DD/MM/YYYY')} (${age} ${yearsLabel ?? 'anos'})`;
}

function normalizeText(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const normalizedValue = value.replace(/\s+/g, ' ').trim();
  return normalizedValue || undefined;
}

function geti18nLabels(t: TFunction) {
  return {
    t,
    reportTitle: t(translations.export.senior_profile.report_title),
    userSectionTitle: t(translations.export.senior_profile.user_section_title),
    profileSectionSubtitle: t(translations.export.senior_profile.profile_section_subtitle),
    fullName: t(translations.export.senior_profile.full_name),
    preferredName: t(translations.export.senior_profile.preferred_name),
    birthDate: t(translations.export.senior_profile.birth_date),
    address: t(translations.export.address),
    snsNumber: t(translations.export.senior_profile.sns_number),
    socialSecurityNumber: t(translations.export.senior_profile.social_security_number),
    taxpayerNumber: t(translations.export.senior_profile.taxpayer_number),
    additionalInformation: t(translations.export.senior_profile.additional_information),
    details: t(translations.export.details),
    healthConditionsSubtitle: t(translations.export.senior_profile.health_conditions_subtitle),
    foodIntolerancesTitle: t(translations.export.senior_profile.food_intolerances_title),
    foodIntolerancesSubtitle: t(translations.export.senior_profile.food_intolerances_subtitle),
    medicationAllergiesSubtitle: t(
      translations.export.senior_profile.medication_allergies_subtitle
    ),
    medicationTitle: t(translations.export.senior_profile.medication_title),
    latestVitalSignsTitle: t(translations.export.senior_profile.latest_vital_signs_title),
    noRecords: t(translations.export.no_records),
    date: t(translations.export.date),
    task: t(translations.export.task),
    register: t(translations.export.register),
    observations: t(translations.export.observations),
    startDate: t(translations.export.start_date),
    endDate: t(translations.export.end_date),
    others: t(translations.export.others),
    years: t(translations.cards.years),
  };
}

type ProfileField = {
  label: string;
  value?: string;
};

type SectionHeaderLayout = {
  titleY: number;
  subtitleY?: number;
  bodyStartY: number;
};

type SeniorProfilePdfLabels = ReturnType<typeof geti18nLabels>;
